/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ComponentFunction.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomDataGridV2 from "../../../common/datagrid/CustomDataGridV2";
import CustomSearchFilterV2 from "../../../common/searchBar/CustomSearchFilterV2";
import CustomToolTip from "../../../../infoButton/CustomToolTip";
import PropTypes from "prop-types";

const formatZoneDetails = (rawZone) => {
  if (Array.isArray(rawZone)) {
    return rawZone
      .map((zone) => zone?.zone_name)
      .filter(Boolean)
      .join(", ");
  }
  if (rawZone?.zone_name) {
    return rawZone.zone_name;
  }
  if (typeof rawZone === "string") {
    return rawZone;
  }
  return "";
};

function ComponentFunction({
  type,
  componentFunction = [],
  searchKeys,
  gap = "2rem",
  parentComponentData = {},
}) {
  const [filteredTableRows, setFilteredTableRows] = useState([]);
  const [filteredData, setFilteredData] = useState(componentFunction);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [slicedList, setSlicedList] = useState([]);

  useEffect(() => {
    setFilteredData(componentFunction);
  }, [componentFunction]);

  useEffect(() => {
    let slice = filteredTableRows.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage,
    );

    setSlicedList(slice);
  }, [page, filteredTableRows, rowsPerPage]);

  const filterRowData = React.useCallback(() => {
    const mappedData =
      filteredData?.map((item, index) => ({
        id: index + 1,
        column1: item,
      })) || [];
    setFilteredTableRows(mappedData);
  }, [filteredData]);

  useEffect(() => {
    filterRowData();
  }, [filterRowData]);

  const COLUMN = [
    {
      field: "column1",
      headerName: "Column 1",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1;
        return (
          <Typography >
            {type === "EE" && (
              <CustomToolTip length={90}>
                {data?.description || ""}
              </CustomToolTip>
            )}
            {type === "SWM" &&
              `${data?.abbreviation || data?.abbrevation || ""} - ${data?.name || ""
              }`}
            {type === "VS" && (
              <CustomToolTip length={90}>
                {data?.description || ""}
              </CustomToolTip>
            )}
            {type === "VC" && (
              <CustomToolTip length={90}>
                {data?.description || ""}
              </CustomToolTip>
            )}
          </Typography>
        );
      },
    },
  ];

  const VS_VEHICLE_COMPONENT_COLUMNS = [
    {
      field: "column1",
      headerName: "Vehicle Component Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const fontSize = "14px";
        const fontColor = "#53565A";
        return (
          <Box sx={{ fontSize }}>
            <Typography
              sx={{ fontWeight: "bold", fontSize, color: fontColor }}
            >{`Assembly Number: ${data?.component_number || "-"}`}</Typography>
            <Typography
              sx={{ fontSize }}
            >{`Name: ${data?.name || "-"}`}</Typography>
            <Typography sx={{ fontSize, color: fontColor }}>
              Description:{" "}
              <CustomToolTip length={90}>
                {data?.description || "-"}
              </CustomToolTip>
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "column2",
      headerName: "Other Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const rawZone =
          data?.zone_details ||
          parentComponentData?.zone_details;
        const zoneDetails = formatZoneDetails(rawZone);
        const suppliers = Array.isArray(data?.suppliers)
          ? data.suppliers.filter(Boolean).join(", ")
          : data?.suppliers;
        const fontSize = "14px";

        return (
          <Box>
            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Supplier:
              </Box>{" "}
              {suppliers || "-"}
            </Typography>
            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Zone Details:
              </Box>{" "}
              {zoneDetails || "-"}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const VC_EE_COMPONENT_COLUMNS = [
    {
      field: "column1",
      headerName: "EE Component Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const fontSize = "14px";
        const fontColor = "#53565A";
        return (
          <Box sx={{ fontSize }}>
            <Typography
              sx={{ fontWeight: "bold", fontSize }}
            >{`Assembly Number: ${data?.assembly_number || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >{`Version: ${data?.version || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >
              Description:{" "}
              <CustomToolTip length={90}>
                {data?.description || "-"}
              </CustomToolTip>
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "column2",
      headerName: "Other Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const rawZone =
          data?.zone_details ||
          parentComponentData?.zone_details;
        const zoneDetails = formatZoneDetails(rawZone);
        const fontSize = "14px";

        return (
          <Box>
            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Supplier:
              </Box>{" "}
              {data?.supplier_name || "-"}
            </Typography>

            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Zone Details:
              </Box>{" "}
              {zoneDetails || "-"}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const EE_SWM_COMPONENT_COLUMNS = [
    {
      field: "column1",
      headerName: "Software Module Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const fontSize = "14px";
        const fontColor = "#53565A";
        return (
          <Box sx={{ fontSize }}>
            <Typography
              sx={{ fontWeight: "bold", fontSize }}
            >{`Name: ${data?.name || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >{`Version: ${data?.version || "-"}`}</Typography>
            <Typography
              sx={{ fontSize, color: fontColor }}
            >
              Description:{" "}
              <CustomToolTip length={90}>
                {data?.description || "-"}
              </CustomToolTip>
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "column2",
      headerName: "Other Details",
      flex: 1,
      renderCell: (params) => {
        const data = params?.row?.column1 || {};
        const rawZone =
          data?.zone_details ||
          parentComponentData?.zone_details;
        const zoneDetails = formatZoneDetails(rawZone);
        const suppliers = Array.isArray(data?.suppliers)
          ? data.suppliers.filter(Boolean).join(", ")
          : (data?.supplier_name || data?.suppliers);
        const fontSize = "14px";

        return (
          <Box>
            {suppliers ? (
              <Typography sx={{ fontSize }}>
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  Supplier:
                </Box>{" "}
                {suppliers || "-"}
              </Typography>
            ) : null}

            {data?.network || data?.network_interface ? (
              <Typography sx={{ fontSize }}>
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  Network:
                </Box>{" "}
                {data?.network || data?.network_interface || "-"}
              </Typography>
            ) : null}

            <Typography sx={{ fontSize }}>
              <Box component="span" sx={{ fontWeight: "bold" }}>
                Zone Details:
              </Box>{" "}
              {zoneDetails || "-"}
            </Typography>
          </Box>
        );
      },
    },
  ];

  if (type === "VS") {
    return (
      <Box
        sx={{
          width: "750px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box mb={2}>
          <CustomSearchFilterV2
            placeholder="Search"
            searchKeys={searchKeys || []}
            getDataOnSearch={(filteredData) => {
              setFilteredData(filteredData);
              setPage(1);
            }}
            inputData={componentFunction}
          />
        </Box>

        <Box sx={{ height: "290px" }}>
          <CustomDataGridV2
            columns={VS_VEHICLE_COMPONENT_COLUMNS}
            rows={slicedList}
            hideHeadder={true}
            isMultiLine={true}
            showPagination={true}
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={filteredTableRows.length}
            handlePageChange={(pageNo) => {
              setPage(pageNo);
            }}
          />
        </Box>
      </Box>
    );
  }

  if (type === "VC") {
    return (
      <Box
        sx={{
          width: "750px",
          display: "flex",
          flexDirection: "column",
          gap: gap,
        }}
      >
        <Box mb={2}>
          <CustomSearchFilterV2
            placeholder="Search"
            searchKeys={searchKeys || []}
            getDataOnSearch={(filteredData) => {
              setFilteredData(filteredData);
              setPage(1);
            }}
            inputData={componentFunction}
          />
        </Box>

        <Box sx={{ height: "290px" }}>
          <CustomDataGridV2
            columns={VC_EE_COMPONENT_COLUMNS}
            rows={slicedList}
            hideHeadder={true}
            isMultiLine={true}
            showPagination={true}
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={filteredTableRows.length}
            handlePageChange={(pageNo) => {
              setPage(pageNo);
            }}
          />
        </Box>
      </Box>
    );
  }

  if (type === "EE") {
    return (
      <Box
        sx={{
          width: "750px",
          display: "flex",
          flexDirection: "column",
          gap: gap,
        }}
      >
        <Box mb={2}>
          <CustomSearchFilterV2
            placeholder="Search"
            searchKeys={searchKeys || []}
            getDataOnSearch={(filteredData) => {
              setFilteredData(filteredData);
              setPage(1);
            }}
            inputData={componentFunction}
          />
        </Box>

        <Box sx={{ height: "290px" }}>
          <CustomDataGridV2
            columns={EE_SWM_COMPONENT_COLUMNS}
            rows={slicedList}
            hideHeadder={true}
            isMultiLine={true}
            showPagination={true}
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={filteredTableRows.length}
            handlePageChange={(pageNo) => {
              setPage(pageNo);
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "750px",
        display: "flex",
        flexDirection: "column",
        gap: gap,
      }}
    >
      <Box mb={2}>
        <CustomSearchFilterV2
          placeholder="Search"
          searchKeys={searchKeys || []}
          getDataOnSearch={(filteredData) => {
            setFilteredData(filteredData);
          }}
          inputData={componentFunction}
        />
      </Box>

      <Box sx={{ height: "270px" }}>
        <CustomDataGridV2
          columns={COLUMN}
          rows={filteredTableRows}
          hideHeader={true}
          page={page}
          rowsPerPage={rowsPerPage}
          showPagination={true}
          totalItems={filteredTableRows.length}
          handlePageChange={(pageNo) => {
            setPage(pageNo);
          }}
        />
      </Box>
    </Box>
  );
}

ComponentFunction.propTypes = {
  type: PropTypes.string,
  componentFunction: PropTypes.array,
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  gap: PropTypes.string,
  parentComponentData: PropTypes.object,
};

export default ComponentFunction;
